import { loadStripe } from '@stripe/stripe-js/pure';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import StripeForm from './StripeForm';
import { trackEvent, trackPixelEvent } from '../../../support-and-tracking/analytics-actions';
import PaymentMethodButton from '../payment-methods/PaymentMethodButton';
import ModalWithHeader from '../../../components/modal-with-header/ModalWithHeader';
import { getFormActions } from '../../../utils/form-helpers';
import stripeFormConfig from '../../../payment/stripe-form-config';
import rewardInfoFormConfig, { getGiverAddress } from '../../../payment/reward-giver-info-form-config';
import paymentFormConfig from '../../../payment/payment-form-config';
import { initiateStripePayment } from '../../../payment/stripe-actions';
import RewardInfoForm from '../reward-info-form/RewardInfoForm';
import { doesAnyRewardNeedAddressOrPhonenumber } from '../cart/cart-model';
import { useUser } from '../../../user/useUser';
import { useProject } from '../../project-hooks/useProject';
import { useSubproject } from '../../../components/hooks/useSubproject';
import { getPathForPostPayment } from '../../../payment/payment-model';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getGreetingCardForPayment } from '../../../payment/payment-selectors';
import LoaderBar from '../../../components/loader-bar/LoaderBar';
import styles from './stripe.module.less';
import I18nText from '../../i18n/I18nText';
import { CREDITCARD_TEXT_KEY, isUserUsingNorwegianLanguageOnProject } from '../../i18n/project-i18n-model';

const stripeFormActions = getFormActions(stripeFormConfig);
const rewardFormActions = getFormActions(rewardInfoFormConfig);
const paymentFormActions = getFormActions(paymentFormConfig);

const Stripe = ({ isCart = false, amount, rewards, disabled = false, giverName, comment }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const projectPollOptionIdString = searchParams.get('projectPollOptionId');
    const projectPollOptionId = projectPollOptionIdString ? parseInt(projectPollOptionIdString, 10) : undefined;
    const project = useProject();
    const { subproject } = useSubproject();
    const stripeKey = useSelector((state) => state.stripeKey);
    const stripeForm = useSelector((state) => state.stripeForm);
    const rewardGiverInfoForm = useSelector((state) => state.rewardGiverInfoForm);
    const maxAmount = useSelector((state) => state.paymentConfig.stripeGiveMaxAmount);
    const user = useUser();
    const email = user?.email || '';
    const stripeEnabled = useSelector((state) => state.features.stripeEnabled) || false;
    const [stripePromise, setStripePromise] = useState(null);
    const stripeCurrentlyUnavailable = useSelector((state) => state.features.stripeCurrentlyUnavailable);
    const greetingCard = useSelector((state) => getGreetingCardForPayment(state, project?.id));

    useEffect(() => {
        setStripePromise(loadStripe(stripeKey, { apiVersion: '2020-08-27' }));
    }, [stripeKey]);

    useEffect(() => {
        return () => {
            dispatch(stripeFormActions.clearAll());
            dispatch(rewardFormActions.clearAll());
        };
    }, [dispatch]);

    const initiatePaymentHandler = () => {
        trackEvent('support', 'checkout-provider', {
            method: 'Stripe',
            'project-id': project.id,
            amount,
            cart: isCart,
            reward: rewards?.length > 0,
            giverName: !!giverName,
            comment: !!comment,
        });
        trackPixelEvent('InitiateCheckout');

        dispatch(stripeFormActions.setFormField('email', email));
        dispatch(stripeFormActions.setFormField('modalIsOpen', true));
    };

    const startStripePayment = ({ stripe, email, paymentMethod }) => {
        const rewardGiverInfoMetadata = doesAnyRewardNeedAddressOrPhonenumber({ rewards })
            ? getGiverAddress(rewardGiverInfoForm)
            : null;

        const norwegian = isUserUsingNorwegianLanguageOnProject(project);

        dispatch(
            initiateStripePayment({
                projectId: project.id,
                title: project.title,
                amount,
                rewards,
                paymentMethod,
                email,
                isFinished,
                rewardGiverInfoMetadata,
                stripe,
                giverName,
                comment,
                subprojectId: subproject?.id,
                projectPollOptionId,
                greetingCard,
                international: !norwegian,
            })
        );
    };

    const isFinished = ({ didGiveSupport, transactionForGiverInfo } = {}) => {
        dispatch(
            paymentFormActions.setFormFields({
                processingPayment: false,
            })
        );
        if (didGiveSupport) {
            navigate(
                getPathForPostPayment({
                    transaction: transactionForGiverInfo,
                    projectId: project?.id,
                })
            );
        }
    };

    const closeModal = () => {
        dispatch(stripeFormActions.clearAll());
        dispatch(rewardFormActions.clearAll());
    };

    if (!stripeEnabled || !project) {
        return null;
    }

    const shouldShowRewardAddressForm =
        doesAnyRewardNeedAddressOrPhonenumber({ rewards }) && !rewardGiverInfoForm.hasEnteredAddress;

    const amountIsGreaterThanStripeMax = amount > maxAmount;

    return (
        <div>
            <div
                className={styles.actionBackdrop}
                aria-hidden
                hidden={!(stripeForm.modalIsOpen && stripeForm.requiresAction)}
            >
                <div className={styles.actionBackdropInner}>
                    <LoaderBar />
                </div>
            </div>
            <PaymentMethodButton
                type="button"
                onClick={initiatePaymentHandler}
                disabled={disabled || amountIsGreaterThanStripeMax || stripeCurrentlyUnavailable}
            >
                <I18nText textKey={CREDITCARD_TEXT_KEY} />
            </PaymentMethodButton>
            <RewardInfoForm
                rewards={rewards}
                modalIsOpen={shouldShowRewardAddressForm && stripeForm.modalIsOpen}
                closeModal={closeModal}
            />
            <ModalWithHeader
                title={project.title}
                titleClass={styles.modalHeaderMaxWidth}
                contentLabel="Viser kortbetaling"
                isOpen={!shouldShowRewardAddressForm && stripeForm.modalIsOpen && !stripeForm.requiresAction}
                onRequestClose={closeModal}
                lightTheme
                shouldCloseOnOverlayClick={false}
            >
                <div className={styles.container}>
                    <Elements stripe={stripePromise}>
                        <StripeForm
                            initiateCallback={startStripePayment}
                            amountInKr={amount}
                            predefinedEmail={email}
                            stripeForm={stripeForm}
                            rewards={rewards}
                            projectId={project.id}
                            isCart={isCart}
                        />
                    </Elements>
                </div>
            </ModalWithHeader>
        </div>
    );
};

Stripe.propTypes = {
    isCart: PropTypes.bool,
    amount: PropTypes.number,
    rewards: PropTypes.array,
};

export default Stripe;
